import { SLOT_BY_KEY, SLOT_KEY_BY_FAMILY, compositionFromFingerprint } from "../compositionSlots";

/**
 * builderDraft — EMAILS-IMPL-015
 *
 * The Email Builder's single source of draft truth: `fingerprint` (the
 * governed Asset-identity register, camelCase, same shape
 * compositionFromFingerprint/CreativeCompositionFields already use) and
 * `canvasLayout` (the ordered, per-occurrence Canvas projection,
 * CRE-SPEC-001 §15.4-§15.9) are always mutated TOGETHER by every function
 * here, so the two representations can never drift apart in Builder state
 * -- there is no code path that updates one without the other where both
 * are implicated (§25).
 *
 * Every function here is pure (draft in, new draft out) and touches
 * nothing but plain JS objects -- no network calls, no React state. This
 * is deliberate: it is the ONE place Creative-Version-to-Builder-draft
 * normalization (§47) and Builder-draft-to-save-payload serialization
 * (§48) happen, so no UI action can hand-build a subtly different shape.
 *
 * Draft shape:
 *   {
 *     fingerprint: { subject, preview, hook, emailBody, offer, cta,
 *                    landingPage, ps, image, imagePresentation },
 *     canvasLayout: [{ blockId, family, assetId, version, role }, ...],
 *   }
 */

// CRE-SPEC-001 §15.8's own deterministic legacy fallback order (Version
// 4.8.0, EMAILS-SPEC-017B -- Image now defaults FIRST, the standard email
// header/logo position), restricted to the eight draggable Canvas body
// families (§8.6) -- Subject/Preview/Landing Page are never part of this
// order; they live only in `fingerprint`, never in `canvasLayout`.
const CANVAS_FALLBACK_ORDER = ["image", "hook", "emailBody", "offer", "cta", "ctaSupportMicrocopy", "ps", "replyInstructions"];

// The four families a governed Creative Version's Fingerprint always
// requires (CRE-SPEC-001 §14) -- their Canvas block may never be removed
// in the Builder, only reordered or have its exact Version changed.
const REQUIRED_CANVAS_FAMILIES = new Set(["HOK", "EML", "OFF", "CTA"]);

function newBlockId() {
  // Matches the existing MissionAssist convention (LandingPageBuilder.jsx,
  // lib/normalizeSnapshot.js) -- browser-native, no new dependency. Falls
  // back when `crypto.randomUUID` isn't available (confirmed empirically:
  // this repo's own Jest/jsdom test environment has no global `crypto` at
  // all, and non-HTTPS browser contexts lack `randomUUID` too) -- still
  // sufficiently unique for per-occurrence Canvas block identity within
  // one Builder session; never a globally governed ID (§15).
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `blk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** One populated {assetId, version} Fingerprint slot -> one Canvas block. */
function blockFromFingerprintSlot(key, ref) {
  const slot = SLOT_BY_KEY[key];
  return { blockId: newBlockId(), family: slot.family, assetId: ref.assetId, version: ref.version, role: slot.family === "CTA" ? "primary" : null };
}

/**
 * CRE-SPEC-001 §15.8: when a Creative Version has no persisted
 * canvas_layout (every historical Version, including CRE0001-CRE0003),
 * synthesize the deterministic default order IN MEMORY ONLY from the
 * Version's own real Fingerprint pins -- never invented content, never
 * persisted merely by computing this.
 */
export function synthesizeFallbackLayout(fingerprint) {
  const blocks = [];
  for (const key of CANVAS_FALLBACK_ORDER) {
    const ref = fingerprint[key];
    if (!ref) continue; // ps/image are optional -- absent means "not on the Canvas"
    blocks.push(blockFromFingerprintSlot(key, ref));
  }
  return blocks;
}

/**
 * Creative-Version-to-Builder-draft normalization (§47). `versionLike` is
 * the backend Creative-Version-shaped object (`{fingerprint, canvas_layout}`,
 * snake_case at the top level, exactly what getCreative/getCreativeVersions
 * return). Uses the PERSISTED canvas_layout when present (mapped to the
 * Builder's camelCase block shape); synthesizes the fallback when absent.
 * Never mutates `versionLike`.
 */
export function normalizeVersionToDraft(versionLike) {
  const fingerprint = compositionFromFingerprint(versionLike?.fingerprint || {});
  const persisted = versionLike?.canvas_layout;
  const canvasLayout = Array.isArray(persisted)
    ? persisted.map((b) => ({ blockId: b.block_id, family: b.family, assetId: b.asset_id, version: b.version, role: b.role || null }))
    : synthesizeFallbackLayout(fingerprint);
  return { fingerprint, canvasLayout };
}

/** True if a normal (non-echo) block of this family already occupies the Canvas. */
export function isFamilyPresent(draft, family) {
  return draft.canvasLayout.some((b) => b.family === family && b.role !== "echo");
}

/**
 * Drag a governed Asset (identified by its composition `key`, e.g.
 * "hook"/"image") from the library onto the Canvas at `insertIndex`. One
 * synchronized operation (§29): populates `fingerprint[key]` AND inserts
 * the Canvas block together. No-ops (returns `draft` unchanged) if that
 * family is already present -- the library UI should already prevent this
 * (§16), this is the defensive backstop.
 */
export function addBlockFromAsset(draft, { key, assetId, version }, insertIndex) {
  const slot = SLOT_BY_KEY[key];
  if (!slot || isFamilyPresent(draft, slot.family)) return draft;

  const newBlock = { blockId: newBlockId(), family: slot.family, assetId, version, role: slot.family === "CTA" ? "primary" : null };
  const canvasLayout = [...draft.canvasLayout];
  const index = insertIndex == null ? canvasLayout.length : Math.max(0, Math.min(insertIndex, canvasLayout.length));
  canvasLayout.splice(index, 0, newBlock);

  return { fingerprint: { ...draft.fingerprint, [key]: { assetId, version } }, canvasLayout };
}

/** §28: required families' normal block may never be removed; echo and optional PS/IMG always may. */
export function canRemoveBlock(draft, blockId) {
  const block = draft.canvasLayout.find((b) => b.blockId === blockId);
  if (!block) return false;
  if (block.role === "echo") return true;
  return !REQUIRED_CANVAS_FAMILIES.has(block.family);
}

/**
 * Removes a Canvas block. For a normal (non-echo) optional block (P.S./
 * Image), also nulls the corresponding Fingerprint slot in the SAME
 * transition (§28) -- removing Image also clears `imagePresentation`,
 * since presentation is only meaningful when `image` is set. No-ops if
 * the block is required or does not exist.
 */
export function removeBlock(draft, blockId) {
  const block = draft.canvasLayout.find((b) => b.blockId === blockId);
  if (!block || !canRemoveBlock(draft, blockId)) return draft;

  const canvasLayout = draft.canvasLayout.filter((b) => b.blockId !== blockId);
  if (block.role === "echo") return { ...draft, canvasLayout };

  const key = SLOT_KEY_BY_FAMILY[block.family];
  const fingerprint = { ...draft.fingerprint, [key]: null };
  if (key === "image") fingerprint.imagePresentation = null;
  return { fingerprint, canvasLayout };
}

/**
 * Reorders the Canvas to `orderedBlockIds` (array position is the sole
 * authoritative order, CRE-SPEC-001 §15.5 -- no separate order field).
 * `block_id` is never regenerated by a reorder. Returns `draft` unchanged
 * (safety backstop) if the id set doesn't match exactly -- a reorder must
 * never silently drop or invent a block.
 */
export function reorderBlocks(draft, orderedBlockIds) {
  const byId = Object.fromEntries(draft.canvasLayout.map((b) => [b.blockId, b]));
  const canvasLayout = orderedBlockIds.map((id) => byId[id]).filter(Boolean);
  if (canvasLayout.length !== draft.canvasLayout.length) return draft;
  return { ...draft, canvasLayout };
}

/**
 * Changes the exact governed Asset/Version a Canvas block (and its
 * matching Fingerprint slot) pins to -- one synchronized transition
 * (§24). A CTA echo's ref is never independently editable (§17, §31):
 * this no-ops for an echo block; changing the PRIMARY CTA's ref instead
 * automatically re-syncs any existing echo to match, so an echo can never
 * point at a different pin than the primary CTA.
 */
export function updateBlockRef(draft, blockId, { assetId, version }) {
  const block = draft.canvasLayout.find((b) => b.blockId === blockId);
  if (!block || block.role === "echo") return draft;

  const canvasLayout = draft.canvasLayout.map((b) => {
    if (b.blockId === blockId) return { ...b, assetId, version };
    if (block.family === "CTA" && b.role === "echo") return { ...b, assetId, version };
    return b;
  });
  const key = SLOT_KEY_BY_FAMILY[block.family];
  return { fingerprint: { ...draft.fingerprint, [key]: { assetId, version } }, canvasLayout };
}

/** Subject/Preview/Landing Page (§8.6 fixed metadata) are never Canvas blocks -- only `fingerprint` changes. */
export function updateFixedSlot(draft, key, ref) {
  return { ...draft, fingerprint: { ...draft.fingerprint, [key]: ref } };
}

/** CRE-SPEC-001 §15.7: Image presentation lives solely on `fingerprint.imagePresentation` -- never a CanvasBlock field. */
export function updateImagePresentation(draft, patch) {
  const current = draft.fingerprint.imagePresentation || { placement: null, width: null, alignment: null };
  return { ...draft, fingerprint: { ...draft.fingerprint, imagePresentation: { ...current, ...patch } } };
}

/**
 * CRE-SPEC-001 §8.8's narrow CTA echo exception: a second Canvas block
 * repeating the SAME primary CTA's exact Asset ID + Version, `role:
 * "echo"`, never a second Fingerprint `cta` reference. No-ops if there is
 * no primary CTA yet, or an echo already exists (at most one, §8.7).
 */
export function addCtaEcho(draft) {
  const primary = draft.canvasLayout.find((b) => b.family === "CTA" && b.role !== "echo");
  if (!primary) return draft;
  if (draft.canvasLayout.some((b) => b.family === "CTA" && b.role === "echo")) return draft;
  const echo = { blockId: newBlockId(), family: "CTA", assetId: primary.assetId, version: primary.version, role: "echo" };
  return { ...draft, canvasLayout: [...draft.canvasLayout, echo] };
}

/**
 * Client-side mirror of the backend's own canvas_layout validation
 * (email_marketing_creative_authoring_service._validate_canvas_layout) --
 * an early-feedback backstop only; the backend remains authoritative.
 */
export function validateDraftForSave(draft) {
  const errors = [];

  for (const key of ["subject", "preview", "hook", "emailBody", "offer", "cta", "landingPage"]) {
    if (!draft.fingerprint[key]) errors.push(`${SLOT_BY_KEY[key].label} is required.`);
  }

  const primaryCtaCount = draft.canvasLayout.filter((b) => b.family === "CTA" && b.role !== "echo").length;
  if (primaryCtaCount !== 1) errors.push("Exactly one primary CTA block is required.");
  const echoCount = draft.canvasLayout.filter((b) => b.family === "CTA" && b.role === "echo").length;
  if (echoCount > 1) errors.push("At most one CTA echo block is allowed.");

  for (const family of ["HOK", "EML", "OFF", "PS", "IMG", "CSM", "RIN"]) {
    const count = draft.canvasLayout.filter((b) => b.family === family).length;
    if (count > 1) errors.push(`Only one ${family} block is allowed.`);
  }

  if (Boolean(draft.fingerprint.ps) !== isFamilyPresent(draft, "PS")) {
    errors.push("P.S. reference and P.S. Canvas block are out of sync.");
  }
  if (Boolean(draft.fingerprint.image) !== isFamilyPresent(draft, "IMG")) {
    errors.push("Image reference and Image Canvas block are out of sync.");
  }
  if (Boolean(draft.fingerprint.ctaSupportMicrocopy) !== isFamilyPresent(draft, "CSM")) {
    errors.push("CTA Support Microcopy reference and Canvas block are out of sync.");
  }
  if (Boolean(draft.fingerprint.replyInstructions) !== isFamilyPresent(draft, "RIN")) {
    errors.push("Reply Instructions reference and Canvas block are out of sync.");
  }

  const ids = draft.canvasLayout.map((b) => b.blockId);
  if (new Set(ids).size !== ids.length) errors.push("Duplicate Canvas block identity detected.");

  return { valid: errors.length === 0, errors };
}

/** Builder-draft-to-save-payload serialization (§48) -- the ONE path that builds a createCreativeVersion/createCreative call's `{composition, canvasLayout}` args. */
export function buildSavePayload(draft) {
  return { composition: draft.fingerprint, canvasLayout: draft.canvasLayout };
}
